import { ReactElement, useState } from 'react';
import { IconButton, Tooltip, Button } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import SaveIcon from '@mui/icons-material/Save';
import { Trans, useTranslation } from 'react-i18next';
import { IsDefaultFlowInSessionStore } from '../services/SessionStoreHelperFunctions';
import ActionDialog from './ActionDialog';
import { DefaultAssignmentsDialog } from './DefaultSelectedConfigurations/DefaultAssignmentsDialog';
import { GetToken, UrlHelper, disableActionButton, isConfigurationComplete } from '../services';
import {
  computeDefaultAssignments,
  countAssignmentItems,
  shouldShowDefaultAssignmentsDialog,
} from '../services/VariableAssignmentsTableHelperFunctions';
import { isFeatureActive } from '../services/ClaimsHelper';
import type { IExtendedConfigureResponse, ISection, IProductAssignments } from '../../types';
import type { IApiErrorShape } from '../../types/IApiTypes';
import type { AxiosResponse } from 'axios';
import { EActionType, ESessionStore, EUrlParams } from '../data/Constants';
import { SessionStore } from '../services/SessionStore';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState, AppStore } from '../store/AppStore';
import { notify, setLoaderMessage } from '../store/states/ConfigurationSlice';
import { setIsConfigurationChanged } from '../store/states/ApplicationSettingsSlice';
import { setError } from '../store/states/ErrorSlice';
import { useNavigate } from 'react-router';

const handleNotifyResponse = (
  res: { payload?: unknown },
  setShowDialog: (value: React.SetStateAction<boolean>) => void,
  handleSuccess: () => void,
  dispatch: AppDispatch
): void => {
  const payload = res.payload as AxiosResponse | IApiErrorShape;
  if ('status' in payload && payload.status === 204) {
    dispatch(setError({ code: 204 }));
    setShowDialog(false);
    dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
  } else if ('error' in payload && payload.error) {
    setShowDialog(false);
  } else {
    handleSuccess();
  }
};

const getSaveAndCloseIconClass = (): string => {
  const disableButton = disableActionButton();
  return disableButton ? 'btn-disabled icon-font-size' : 'notify-icon icon-font-size';
};

export const SaveAndClose = ({
  configuration,
}: {
  configuration: IExtendedConfigureResponse;
}): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialog, setDialog] = useState<string | ReactElement>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [header, setHeader] = useState('');
  const [dialogType, setDialogType] = useState('');
  const [showDefaultAssignmentsDialog, setShowDefaultAssignmentsDialog] = useState(false);
  const [defaultAssignmentsData, setDefaultAssignmentsData] = useState<{
    assignments: IProductAssignments;
    lessAssignments: IProductAssignments;
  }>({ assignments: {}, lessAssignments: {} });

  const dispatch = useDispatch<AppDispatch>();
  const claimsData = AppStore.getState().claimsData;
  const isCustomerPricingActive = useSelector((state: AppState): boolean =>
    isFeatureActive(state.claimsData.featureFlags.CustomerPricing)
  );

  const disableButton = disableActionButton();
  const configurationId = UrlHelper.getSearchParameter(EUrlParams.ConfigurationId);
  const { token, auth } = GetToken();
  const isConfigurationChanged = useSelector(
    (state: AppState): boolean => state.applicationSettings.isConfigurationChanged
  );

  const onNotifyDialog = (): void => {
    // Skip completeness check when AllowPartialConfiguration is active.
    const isCompleteConfiguration = isFeatureActive(
      claimsData.featureFlags.AllowPartialConfiguration
    )
      ? true
      : isConfigurationComplete(configuration?.data?.sections as ISection[]);
    if (isCompleteConfiguration) {
      setDialogType(EActionType.Notify);
      // Check if feature flag is enabled (computed only when dialog is triggered)
      const isFeatureDisplayConfigurationStateEnabled = isFeatureActive(
        claimsData.featureFlags.DisplayConfigurationState
      );

      // Compute default assignments only when needed (on button click, not on every render)
      const { assignments: currentAssignmentsByDefault, lessAssignments } =
        isFeatureDisplayConfigurationStateEnabled && configuration.data?.sections
          ? computeDefaultAssignments(configuration)
          : { assignments: {}, lessAssignments: {} };

      // Check if we should intercept save with default assignments dialog
      const canShowDefaultDialog = shouldShowDefaultAssignmentsDialog(
        isFeatureDisplayConfigurationStateEnabled,
        countAssignmentItems(currentAssignmentsByDefault) + countAssignmentItems(lessAssignments)
      );
      if (canShowDefaultDialog) {
        setDefaultAssignmentsData({
          assignments: currentAssignmentsByDefault,
          lessAssignments,
        });
        setShowDefaultAssignmentsDialog(true);
        return;
      } else {
        setHeader(t('closeWithSaveDialog.closeWithSaveChanges'));
        setDialog(t('closeWithSaveDialog.closeWithSaveConfirmationMessage'));
      }
    } else {
      setDialogType(EActionType.IncompleteConfiguration);
      setHeader(t('incompleteConfigurationDialog.incompleteConfiguration'));
      setDialog(
        <Trans
          i18nKey="incompleteConfigurationDialog.incompleteConfigurationMessage"
          components={{ 1: <span style={{ color: 'red' }} /> }}
        />
      );
    }
    // To Reset Error Message while opening dialog
    setErrorMessage('');
    setShowDialog(true);
  };

  const onCloseDialog = (): void => {
    setDialog('');
    setShowDialog(false);
  };

  // Method to call the Notify API and handle error if Error Occurs
  const action = (): void => {
    if (dialogType === EActionType.Notify) {
      notifyAPICall();
    }
  };

  const notifyAPICall = (): void => {
    setShowDialog(false);
    setErrorMessage('');
    dispatch(setLoaderMessage(EActionType.Notify));
    dispatch(
      notify({ configurationId: SessionStore.get(ESessionStore.ConfigurationId), token: token })
    ).then((res): void => {
      handleNotifyResponse(res, setShowDialog, handleSuccess, dispatch);
      if (!IsDefaultFlowInSessionStore()) {
        globalThis.parent.postMessage(
          { message: 'configuration_saved', cid: configurationId },
          document.referrer
        );
      }
    });
  };

  const handlePriceChangeButtonClick = (): void => {
    setShowDialog(false);
    setErrorMessage('');
    dispatch(setLoaderMessage(EActionType.Notify));
    console.log('Update price button clicked');

    dispatch(
      notify({ configurationId: SessionStore.get(ESessionStore.ConfigurationId), token: token })
    ).then((res): void => {
      handleNotifyResponse(res, setShowDialog, handlePriceChangeSuccess, dispatch);
      globalThis.parent.postMessage(
        { message: 'configuration_quick_saved', cid: configurationId },
        document.referrer
      );
    });
  };

  const handlePriceChangeSuccess = (): void => {
    dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
    dispatch(setLoaderMessage(null));
  };

  const handleSuccess = (): void => {
    handleNotifySuccess();
    dispatch(setIsConfigurationChanged({ isConfigurationChanged: false }));
    dispatch(setLoaderMessage(null));
  };

  function handleNotifySuccess(): void {
    if (IsDefaultFlowInSessionStore()) {
      setDialogType(EActionType.Success);
      setHeader(t('dialogBox.success'));
      setDialog(t('successMessages.notifySuccess'));
      setShowDialog(true);
    } else {
      closeTab();
      setShowDialog(false);
    }
  }

  const closeTab = (): void => {
    onCloseDialog();
    if (dialogType === EActionType.Notify) {
      navigate('/notified', { replace: true });
    } else {
      navigate('/closeTab', { replace: true });
      globalThis.parent.postMessage(
        { message: 'configuration_close', cid: configurationId },
        document.referrer
      );
    }
    setTimeout((): void => {
      auth?.signOut();
      SessionStore.clear();
    }, 2000);
  };

  return (
    <>
      {IsDefaultFlowInSessionStore() ? (
        <Tooltip title={t('tooltip.saveAndClose')}>
          <span>
            <IconButton
              onClick={onNotifyDialog}
              disabled={disableButton}
              className={`action-panel-buttons ${getSaveAndCloseIconClass()}`}
              data-testid="btn-save-close-configuration"
            >
              <SaveIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <div className="product-action-buttons" data-testid="product-action-buttons">
          <Button
            variant="outlined"
            startIcon={<AddShoppingCartIcon className="cart-icon" />}
            onClick={onNotifyDialog}
            sx={{ margin: '0px !important' }}
            disabled={disableButton}
            data-testid="btn-save-close-configuration"
            className={disableButton ? 'disable-product-action' : 'add-product-action'}
          >
            <span>{t('labels.addProductToCart')}</span>
          </Button>

          {isCustomerPricingActive && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PriceChangeIcon className="cart-icon" />}
              onClick={(): void => handlePriceChangeButtonClick()}
              sx={{ margin: '0px !important' }}
              disabled={!isConfigurationChanged}
              data-testid="btn-update-price-configuration"
              className={
                disableButton || !isConfigurationChanged
                  ? 'disable-update-price-action'
                  : 'update-price-action'
              }
            >
              <span>{t('labels.updatePrice')}</span>
            </Button>
          )}
        </div>
      )}
      <ActionDialog
        showDialog={showDialog}
        onCloseDialog={onCloseDialog}
        header={header}
        errorMessage={errorMessage}
        dialog={dialog}
        action={action}
        dialogType={dialogType}
      />
      {showDefaultAssignmentsDialog && (
        <DefaultAssignmentsDialog
          assignmentsByDefault={defaultAssignmentsData.assignments}
          lessAssignments={defaultAssignmentsData.lessAssignments}
          onOk={(): void => {
            setShowDefaultAssignmentsDialog(false);
            notifyAPICall();
          }}
          onCancel={(): void => {
            setShowDefaultAssignmentsDialog(false);
          }}
        />
      )}
    </>
  );
};
